import { useMemo } from "react";
import { Flex, Spinner, Text } from "@radix-ui/themes";
import { CheckIcon, Cross1Icon } from "@radix-ui/react-icons";
import { useGetFrameAvailabilityQuery } from "@/shared/api/insights-api";
import { useGondolaChangeGroupsData } from "../hooks";

type AvailabilityState = "loading" | "available" | "unavailable" | "unknown";

const AVAILABILITY_CONFIG = {
  loading: {
    message: "Checking frame availability...",
    icon: <Spinner size="1" className="text-white" />,
    textColor: "text-white",
  },
  available: {
    message: "Frame Available",
    icon: <CheckIcon className="text-green-400" />,
    textColor: "text-green-400",
  },
  unavailable: {
    message: "Frame Not Available",
    icon: <Cross1Icon className="text-red-400" />,
    textColor: "text-red-400",
  },
  unknown: {
    message: "Frame availability unknown",
    icon: <Cross1Icon className="text-white" />,
    textColor: "text-white",
  },
} as const;

function getAvailabilityState(
  isLoading: boolean,
  frameFound?: boolean
): AvailabilityState {
  if (isLoading) return "loading";
  if (frameFound === true) return "available";
  if (frameFound === false) return "unavailable";
  return "unknown";
}

export function FrameAvailabilityStatus() {
  const { selectedGondolaChange } = useGondolaChangeGroupsData();

  const queryParams = useMemo(
    () => ({
      frame_start_time: selectedGondolaChange?.frame_start_time || "",
      frame_end_time: selectedGondolaChange?.frame_end_time || "",
      camera_local_id: selectedGondolaChange?.camera_local_id || "",
    }),
    [selectedGondolaChange]
  );

  const { data, isLoading, isFetching } = useGetFrameAvailabilityQuery(
    queryParams,
    { skip: !selectedGondolaChange }
  );

  if (!selectedGondolaChange) return null;

  const state = getAvailabilityState(
    isLoading || isFetching,
    data?.result?.frame_found
  );
  const config = AVAILABILITY_CONFIG[state];

  return (
    <Flex
      align="center"
      justify="center"
      gap="2"
      className="bg-black bg-opacity-60 px-3 py-2 rounded-md"
    >
      <Flex className="w-4 h-4" align="center" justify="center">
        {config.icon}
      </Flex>
      <Text className={config.textColor} highContrast size="2">
        {config.message}
      </Text>
    </Flex>
  );
}
